if (!customElements.get('quick-add-modal')) {
  /* ALT-QUICKVIEW-003 — perceived latency.
     `show()` used to fetch first and open second, so nothing moved until the
     network round-trip resolved (measured: at 260ms after a real click the
     drawer had not begun to move). Two changes, both here, none in the engine:
     the panel now opens on the click and swaps a skeleton for the content, and
     the fetch starts on hover/focus intent so the common case is already
     resolved by the time the button is pressed.

     The cache holds PROMISES, not text, so two intents for the same product
     share one request. Failures are evicted so a flaky network is retried, and
     everything is dropped on cart-update: a fragment carries price and
     availability, which a cart mutation can change. */
  const quickViewCache = new Map();

  const quickViewSectionUrl = (url) =>
    `${url}${url.includes('?') ? '&' : '?'}section_id=cart-quick-view`;

  const fetchQuickView = (url) => {
    if (quickViewCache.has(url)) return quickViewCache.get(url);

    const request = fetch(quickViewSectionUrl(url)).then((response) => {
      if (!response.ok) throw new Error(`Quick view ${response.status}`);
      return response.text();
    });

    quickViewCache.set(url, request);
    request.catch(() => quickViewCache.delete(url));
    return request;
  };

  const openerUrlFrom = (target) => {
    const button = target instanceof Element ? target.closest('[data-modal="#CartQuickView"] [data-product-url]') : null;
    return button ? button.getAttribute('data-product-url') : null;
  };

  /* pointerover (not pointerenter) so one delegated listener covers openers that
     cart-recommendations.js injects later. Never fires on load — intent only. */
  const prefetchOnIntent = (event) => {
    const url = openerUrlFrom(event.target);
    if (url) fetchQuickView(url);
  };

  document.addEventListener('pointerover', prefetchOnIntent, { passive: true });
  document.addEventListener('focusin', prefetchOnIntent);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => quickViewCache.clear());
  }

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.removeAttribute('data-loading');
        this.querySelector('[role="dialog"]')?.removeAttribute('aria-busy');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        /* ALT-QUICKVIEW-001 — fetch ONLY the quick-view section.
           This used to request the entire product page and scrape <product-info>
           out of it: ~200KB of HTML to render a buy box, which also dragged the
           PDP's breadcrumb and page chrome into the panel. The Section Rendering
           API returns just sections/cart-quick-view.liquid, so the payload is a
           fragment and the markup is ours. None of Dawn's post-scrape cleanup
           (preventDuplicatedIDs / removeDOMElements / updateImageSizes) is needed
           any more, because there is nothing foreign to clean up. */
        const url = opener.getAttribute('data-product-url');
        const dialog = this.querySelector('[role="dialog"]');
        /* ALT-QUICKVIEW-003: a hover or focus already warmed this one, so it can
           open straight into real content with no skeleton flash. */
        const alreadyFetched = quickViewCache.has(url);
        const request = fetchQuickView(url);

        if (!alreadyFetched) {
          this.modalContent.innerHTML = '';
          this.setAttribute('data-loading', 'true');
          dialog?.setAttribute('aria-busy', 'true');
          super.show(opener);
        }

        request
          .then((responseText) => {
            HTMLUpdateUtility.setInnerHTML(this.modalContent, responseText);

            if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();

            this.removeAttribute('data-loading');
            dialog?.removeAttribute('aria-busy');

            if (alreadyFetched) super.show(opener);
          })
          .catch(() => {
            // Never strand the shopper inside a dead panel: fall back to the PDP.
            this.removeAttribute('data-loading');
            dialog?.removeAttribute('aria-busy');
            window.location.href = url;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
